import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpService } from 'src/app/services/http.service';

/** Mirrors DTO.Geology.LithologyDTO */
export interface LithologyRef {
  id:          number;
  code:        number;
  name:        string;
  rockType:    string;
  description: string;
  color:       string;
  regionId:    number;
  siteId?:     number | null;
  isActive:    boolean;
}

/** Mirrors DTO.Geology.DiscontinuityDTO — shown as "Drill log" entries in the UI */
export interface DiscontinuityRef {
  id:        number;
  name:      string;
  shortName: string;
  color:     string;
  regionId:  number;
  siteId?:   number | null;
  isActive:  boolean;
}

interface SiteGeologyCache {
  lithologies?:     Promise<LithologyRef[]>;
  discontinuities?: Promise<DiscontinuityRef[]>;
}

/**
 * Site-scoped geology reference lists.
 * The API's `Geology/site/{siteId}/…` endpoints already merge the area (region) items
 * with the site's local overrides, so callers get one resolved list per site.
 * Results are cached per site for the session; call `invalidate` after editing the lists.
 */
@Injectable({ providedIn: 'root' })
export class GeologyReferenceService {
  private readonly http = inject(HttpService);
  private readonly cache = new Map<number, SiteGeologyCache>();

  lithologies(siteId: number): Promise<LithologyRef[]> {
    const entry = this.entry(siteId);
    entry.lithologies ??= this.fetch<LithologyRef>(`Geology/site/${siteId}/lithologies`)
      .catch(error => { entry.lithologies = undefined; throw error; });
    return entry.lithologies;
  }

  discontinuities(siteId: number): Promise<DiscontinuityRef[]> {
    const entry = this.entry(siteId);
    entry.discontinuities ??= this.fetch<DiscontinuityRef>(`Geology/site/${siteId}/discontinuities`)
      .catch(error => { entry.discontinuities = undefined; throw error; });
    return entry.discontinuities;
  }

  invalidate(siteId?: number): void {
    if (siteId === undefined) this.cache.clear();
    else this.cache.delete(siteId);
  }

  private entry(siteId: number): SiteGeologyCache {
    let entry = this.cache.get(siteId);
    if (!entry) {
      entry = {};
      this.cache.set(siteId, entry);
    }
    return entry;
  }

  /**
   * Active items only, in two groups: the area (region) items first, then the site-level overrides
   * (`siteId` set), each group sorted by name.
   */
  private async fetch<T extends { isActive: boolean; name: string; siteId?: number | null }>(url: string): Promise<T[]> {
    const items = await firstValueFrom(this.http.get<T[]>(url));
    const group = (item: T): number => (item.siteId == null ? 0 : 1);
    return (items ?? [])
      .filter(item => item.isActive !== false)
      .sort((a, b) => group(a) - group(b) || a.name.localeCompare(b.name));
  }
}
